import React from 'react';
import { Box, Typography, Button } from '@common-ui-library/atom';
import { useI18n } from '@common-ui-library/shared';
import { EleosLogo } from '@common-ui-library/molecule';

interface AuthErrorPageProps {
  error: string;
  errorDescription: string;
  onRetry: () => void;
}

/**
 * Convert snake_case error descriptions to human-readable text.
 * e.g., "username_missing" -> "Username Missing"
 */
const formatErrorDescription = (description: string): string => {
  return decodeURIComponent(description)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const AuthErrorPage: React.FC<AuthErrorPageProps> = ({
  error,
  errorDescription,
  onRetry,
}) => {
  const { t } = useI18n();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <EleosLogo />
      </Box>
      <Typography variant="h4" gutterBottom>
        {t('errors.authenticationFailed')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 500 }}>
        {t('errors.authErrorMessage')}
      </Typography>
      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 1,
          p: 2,
          mb: 4,
          maxWidth: 500,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>{t('labels.error')}:</strong> {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t('labels.details')}:</strong>{' '}
          {formatErrorDescription(errorDescription)}
        </Typography>
      </Box>
      <Button variant="contained" color="primary" onClick={onRetry} sx={{ mb: 2 }}>
        {t('actions.tryAgain')}
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
        {t('errors.contactSupport')}
      </Typography>
    </Box>
  );
};

